/**
 * Greek → Latin romanization: Modern Greek in ELOT 743 and ALA-LC, Ancient and Medieval
 * Greek in the classical convention and ALA-LC. Polytonic and monotonic input both work —
 * text is decomposed (NFD) and every letter keeps its breathing, accent, diaeresis and iota
 * subscript as flags.
 *
 * Sources, each read in full:
 *
 * - ELOT 743 — "Romanization of Greek: ELOT 743 System; BGN/PCGN 1996 Agreement" (checked
 *   October 2017), gov.uk/government/publications/romanization-systems. It is the Hellenic
 *   Organization for Standardization's system that the Fifth UN Conference on the
 *   Standardization of Geographical Names approved in 1987. Stress is shown with an acute;
 *   αυ/ευ/ηυ are av/ev/iv before voiced sounds and vowels, af/ef/if before voiceless
 *   consonants and at the end of a word, and ay/ey/iy when the first vowel is stressed or υ
 *   has a diaeresis; μπ is b at the start and end of a word and mp inside it.
 * - ALA-LC — Library of Congress "Greek" romanization table (July 2010). Texts before 1454
 *   count as Ancient or Medieval (β = b), later ones as Modern (β = v). Rough breathing is h,
 *   supplied where the text does not show it — which an algorithm can do only for initial υ
 *   (and, in Ancient Greek, initial ρ), the two letters that always carried it. Accents,
 *   smooth breathing, the diaeresis and iota subscript and adscript are not romanized; γκ is
 *   gk at the start and end of a word and nk inside it; ντ at the start of a word is d̲.
 *   Greek numerals become Arabic numerals.
 * - Classical — The SBL Handbook of Style, 2nd ed., §5.3 (η = ē, ω = ō, gamma nasal = n,
 *   initial ῥ and the second ρ of ρρ = rh, υ = u in diphthongs and y elsewhere, rough
 *   breathing = h). SBL makes no provision for iota subscript; The Chicago Manual of Style
 *   writes it as i on the line after its vowel (ἀνθρώπῳ → anthrōpōi; 17th ed. §11.110), which
 *   is the alternative offered here.
 */

export type GreekSystem =
  | "elot"
  | "ala-lc-modern"
  | "classical"
  | "ala-lc-ancient";
export type GreekPeriod = "modern" | "ancient";
export type IotaSubscript = "omit" | "i";

export const SYSTEMS_FOR_PERIOD: Record<GreekPeriod, readonly GreekSystem[]> = {
  modern: ["elot", "ala-lc-modern"],
  ancient: ["classical", "ala-lc-ancient"],
};

interface Glyph {
  /** Lower-case base letter, final sigma folded into σ. */
  base: string;
  upper: boolean;
  accent: boolean;
  rough: boolean;
  diaeresis: boolean;
  iotaSub: boolean;
}

type Token =
  | { kind: "word"; glyphs: Glyph[] }
  | { kind: "other"; text: string };

const ACUTE = "\u0301";
const DIAERESIS = "\u0308";
const LOW_LINE = "\u0332";
const DOT_BELOW = "\u0323";

const GREEK_LETTER = /^[Ͱ-Ͽ]$/;
const LETTERS = new Set([..."αβγδεζηθικλμνξοπρσςτυφχψωϝϙϟϛϡ"]);
const VOWELS = new Set([..."αεηιουω"]);
/** Letters that make a preceding αυ/ευ/ηυ voiceless in ELOT 743: af, ef, if. */
const VOICELESS = new Set([..."θκξπστφχψ"]);
/** Variant letter forms folded into the ordinary letter. */
const VARIANTS: Record<string, string> = {
  ϐ: "β",
  ϑ: "θ",
  ϕ: "φ",
  ϖ: "π",
  ϱ: "ρ",
  ϵ: "ε",
  ϲ: "σ",
  ϰ: "κ",
  ς: "σ",
};
const KERAIA = new Set(["ʹ", "´", "′"]);
const LOWER_KERAIA = "͵";

/* -------------------------------------------------------------------- tokenizing */

function markGlyph(glyph: Glyph, mark: string): void {
  switch (mark) {
    case "\u0301":
    case "\u0300":
    case "\u0342":
      glyph.accent = true;
      break;
    case "\u0314":
      glyph.rough = true;
      break;
    case "\u0308":
      glyph.diaeresis = true;
      break;
    case "\u0345":
      glyph.iotaSub = true;
      break;
    default:
      break;
  }
}

function newGlyph(char: string): Glyph {
  const lower = char.toLowerCase();
  return {
    base: VARIANTS[lower] ?? lower,
    upper: lower !== char,
    accent: false,
    rough: false,
    diaeresis: false,
    iotaSub: false,
  };
}

function isGreekLetter(char: string): boolean {
  if (!GREEK_LETTER.test(char)) {
    return false;
  }
  const lower = char.toLowerCase();
  return LETTERS.has(lower) || lower in VARIANTS;
}

const COMBINING = /^[\u0300-\u036F]$/;

export function tokenizeGreek(text: string): Token[] {
  const tokens: Token[] = [];
  let word: Glyph[] | null = null;
  let other = "";
  for (const char of text.normalize("NFD")) {
    if (isGreekLetter(char)) {
      if (other) {
        tokens.push({ kind: "other", text: other });
        other = "";
      }
      word ??= [];
      word.push(newGlyph(char));
      continue;
    }
    const last = word?.at(-1);
    if (last && COMBINING.test(char)) {
      markGlyph(last, char);
      continue;
    }
    if (word) {
      tokens.push({ kind: "word", glyphs: word });
      word = null;
    }
    other += char;
  }
  if (word) {
    tokens.push({ kind: "word", glyphs: word });
  }
  if (other) {
    tokens.push({ kind: "other", text: other });
  }
  return tokens;
}

/**
 * Iota adscript: a capital α, η or ω carrying a breathing or accent and followed by a bare ι
 * (Ἅιδῃ). In a true diphthong the marks sit on the ι instead (Αἵ).
 */
function foldAdscript(glyphs: Glyph[]): Glyph[] {
  const out: Glyph[] = [];
  for (let index = 0; index < glyphs.length; index++) {
    const glyph = glyphs[index];
    const next = glyphs[index + 1];
    const marked = glyph.rough || glyph.accent;
    if (
      glyph.upper &&
      marked &&
      "αηω".includes(glyph.base) &&
      next?.base === "ι" &&
      !next.upper &&
      !(next.accent || next.rough || next.diaeresis)
    ) {
      out.push({ ...glyph, iotaSub: true });
      index += 1;
      continue;
    }
    out.push(glyph);
  }
  return out;
}

/* ------------------------------------------------------------------- numerals */

const NUMERAL_VALUES: Record<string, number> = {
  α: 1,
  β: 2,
  γ: 3,
  δ: 4,
  ε: 5,
  ϛ: 6,
  ϝ: 6,
  ζ: 7,
  η: 8,
  θ: 9,
  ι: 10,
  κ: 20,
  λ: 30,
  μ: 40,
  ν: 50,
  ξ: 60,
  ο: 70,
  π: 80,
  ϙ: 90,
  ϟ: 90,
  ρ: 100,
  σ: 200,
  τ: 300,
  υ: 400,
  φ: 500,
  χ: 600,
  ψ: 700,
  ω: 800,
  ϡ: 900,
};

/** A Greek numeral (letters followed by the keraia ʹ) as its value, or null. */
function numeralValue(
  glyphs: readonly Glyph[],
  thousands: boolean
): number | null {
  const letters = glyphs.map((glyph) => glyph.base).join("");
  if (letters === "στ") {
    return 6;
  }
  let total = 0;
  for (const [index, letter] of [...letters].entries()) {
    const value = NUMERAL_VALUES[letter];
    if (value === undefined) {
      return null;
    }
    total += thousands && index === 0 ? value * 1000 : value;
  }
  return total;
}

/* ---------------------------------------------------------------- shared output */

interface Unit {
  text: string;
  upper: boolean;
}

function joinUnits(units: readonly Unit[], allCaps: boolean): string {
  const text = units
    .map((unit) => {
      if (!unit.upper || unit.text === "") {
        return unit.text;
      }
      return allCaps
        ? unit.text.toUpperCase()
        : unit.text.charAt(0).toUpperCase() + unit.text.slice(1);
    })
    .join("");
  return text.normalize("NFC");
}

function isAllCaps(glyphs: readonly Glyph[]): boolean {
  return glyphs.length > 1 && glyphs.every((glyph) => glyph.upper);
}

/* ------------------------------------------------------- ALA-LC and classical */

interface ScholarlyOptions {
  ancient: boolean;
  classical: boolean;
  iotaSubscript: IotaSubscript;
}

const PLAIN: Record<string, string> = {
  α: "a",
  β: "b",
  γ: "g",
  δ: "d",
  ε: "e",
  ζ: "z",
  η: "ē",
  θ: "th",
  ι: "i",
  κ: "k",
  λ: "l",
  μ: "m",
  ν: "n",
  ξ: "x",
  ο: "o",
  π: "p",
  ρ: "r",
  σ: "s",
  τ: "t",
  υ: "y",
  φ: "ph",
  χ: "ch",
  ψ: "ps",
  ω: "ō",
  ϝ: "w",
  ϙ: `k${DOT_BELOW}`,
  ϟ: `k${DOT_BELOW}`,
};

const SECOND_U = new Set(["αυ", "ευ", "ηυ", "ου", "ωυ"]);
const SECOND_I = new Set(["αι", "ει", "οι", "υι"]);

function isDiphthong(
  glyph: Glyph,
  next: Glyph | undefined,
  breakOnAccent: boolean
): boolean {
  if (!next || next.diaeresis) {
    return false;
  }
  const pair = glyph.base + next.base;
  if (!(SECOND_U.has(pair) || SECOND_I.has(pair))) {
    return false;
  }
  return !(breakOnAccent && glyph.accent);
}

function vowelText(glyph: Glyph, inDiphthong: boolean): string {
  if (glyph.base === "υ") {
    return inDiphthong ? "u" : "y";
  }
  return PLAIN[glyph.base];
}

/** γ before γ, κ, ξ, χ: the gamma nasal. */
function gammaText(
  glyphs: readonly Glyph[],
  index: number,
  options: ScholarlyOptions
): string {
  const next = glyphs[index + 1]?.base;
  if (next === "γ" || next === "ξ" || next === "χ") {
    return "n";
  }
  if (next === "κ") {
    // ALA-LC: gk at the start and the end of a word, nk inside it.
    const edge = index === 0 || index + 2 === glyphs.length;
    return !options.classical && edge ? "g" : "n";
  }
  return "g";
}

function rhoText(
  glyphs: readonly Glyph[],
  index: number,
  options: ScholarlyOptions
): string {
  const glyph = glyphs[index];
  if (glyph.rough) {
    return "rh";
  }
  if (options.ancient && index === 0) {
    return "rh";
  }
  if (options.classical && glyphs[index - 1]?.base === "ρ") {
    return "rh";
  }
  return "r";
}

/** One consonant, or a consonant cluster that romanizes as a unit (μπ, ντ). */
function consonantUnit(
  glyphs: readonly Glyph[],
  index: number,
  options: ScholarlyOptions
): { text: string; length: number } {
  const glyph = glyphs[index];
  const next = glyphs[index + 1]?.base;
  if (!options.classical && index === 0 && glyph.base === "μ" && next === "π") {
    return { text: "b", length: 2 };
  }
  if (!options.classical && index === 0 && glyph.base === "ν" && next === "τ") {
    return { text: `d${LOW_LINE}`, length: 2 };
  }
  if (glyph.base === "β") {
    return { text: options.ancient ? "b" : "v", length: 1 };
  }
  if (glyph.base === "γ") {
    return { text: gammaText(glyphs, index, options), length: 1 };
  }
  if (glyph.base === "ρ") {
    return { text: rhoText(glyphs, index, options), length: 1 };
  }
  return { text: PLAIN[glyph.base] ?? glyph.base, length: 1 };
}

function vowelUnit(
  glyphs: readonly Glyph[],
  index: number,
  options: ScholarlyOptions
): { text: string; length: number } {
  const glyph = glyphs[index];
  const next = glyphs[index + 1];
  const diphthong = isDiphthong(glyph, next, !options.ancient);
  const group = diphthong && next ? [glyph, next] : [glyph];
  // Initial υ always had a rough breathing, so h is supplied when the text omits it.
  const supplied = index === 0 && glyph.base === "υ";
  const rough = supplied || group.some((each) => each.rough);
  let text = rough ? "h" : "";
  for (const each of group) {
    text += vowelText(each, diphthong);
    if (each.iotaSub && options.classical && options.iotaSubscript === "i") {
      text += "i";
    }
  }
  return { text, length: group.length };
}

function scholarlyWord(
  glyphs: readonly Glyph[],
  options: ScholarlyOptions
): Unit[] {
  const units: Unit[] = [];
  let index = 0;
  while (index < glyphs.length) {
    const glyph = glyphs[index];
    const unit = VOWELS.has(glyph.base)
      ? vowelUnit(glyphs, index, options)
      : consonantUnit(glyphs, index, options);
    units.push({ text: unit.text, upper: glyph.upper });
    index += unit.length;
  }
  return units;
}

/* -------------------------------------------------------------------- ELOT 743 */

const ELOT: Record<string, string> = {
  ...PLAIN,
  β: "v",
  η: "i",
  φ: "f",
  ω: "o",
};

/** A vowel letter carrying the Greek letter's own accent and diaeresis. */
function elotVowel(
  letter: string,
  glyph: Glyph,
  accent = glyph.accent
): string {
  return letter + (glyph.diaeresis ? DIAERESIS : "") + (accent ? ACUTE : "");
}

/** αυ, ευ, ηυ: av/ef/… unless the first vowel is stressed or υ has a diaeresis. */
function elotVowelPair(
  glyphs: readonly Glyph[],
  index: number
): { text: string; length: number } | null {
  const glyph = glyphs[index];
  const next = glyphs[index + 1];
  if (!next || next.base !== "υ" || next.diaeresis || glyph.accent) {
    return null;
  }
  if (glyph.base === "ο") {
    return { text: `o${elotVowel("u", next)}`, length: 2 };
  }
  if (!"αεη".includes(glyph.base)) {
    return null;
  }
  const after = glyphs[index + 2]?.base;
  const voiceless = after === undefined || VOICELESS.has(after);
  // The stress written on υ moves to the vowel before it: Αύρα → Ávra.
  const first = elotVowel(ELOT[glyph.base], glyph, next.accent);
  return { text: first + (voiceless ? "f" : "v"), length: 2 };
}

function elotUnit(
  glyphs: readonly Glyph[],
  index: number
): { text: string; length: number } {
  const glyph = glyphs[index];
  const next = glyphs[index + 1]?.base;
  if (VOWELS.has(glyph.base)) {
    const pair = elotVowelPair(glyphs, index);
    if (pair) {
      return pair;
    }
    return { text: elotVowel(ELOT[glyph.base], glyph), length: 1 };
  }
  if (glyph.base === "γ" && (next === "γ" || next === "ξ" || next === "χ")) {
    return { text: "n", length: 1 };
  }
  if (glyph.base === "μ" && next === "π") {
    const edge = index === 0 || index + 2 === glyphs.length;
    return edge ? { text: "b", length: 2 } : { text: "m", length: 1 };
  }
  return { text: ELOT[glyph.base] ?? glyph.base, length: 1 };
}

function elotWord(glyphs: readonly Glyph[]): Unit[] {
  const units: Unit[] = [];
  let index = 0;
  while (index < glyphs.length) {
    const unit = elotUnit(glyphs, index);
    units.push({ text: unit.text, upper: glyphs[index].upper });
    index += unit.length;
  }
  return units;
}

/* ------------------------------------------------------------------------ public */

export interface GreekOptions {
  iotaSubscript: IotaSubscript;
}

function romanizeWord(
  glyphs: Glyph[],
  system: GreekSystem,
  options: GreekOptions
): string {
  if (system === "elot") {
    return joinUnits(elotWord(glyphs), isAllCaps(glyphs));
  }
  const folded = foldAdscript(glyphs);
  const units = scholarlyWord(folded, {
    ancient: system !== "ala-lc-modern",
    classical: system === "classical",
    iotaSubscript: options.iotaSubscript,
  });
  return joinUnits(units, isAllCaps(folded));
}

/**
 * Punctuation right after a Greek word: the Greek question mark (normalised to ;) becomes ?,
 * the raised dot (ano teleia) becomes ;, and every elision mark becomes ’.
 */
const PUNCTUATION_AFTER_GREEK: Record<string, string> = {
  ";": "?",
  "·": ";",
  "'": "’",
  "’": "’",
  ʼ: "’",
  "᾽": "’",
};

function convertPunctuation(text: string, afterGreek: boolean): string {
  const replacement = afterGreek
    ? PUNCTUATION_AFTER_GREEK[text.charAt(0)]
    : undefined;
  return replacement === undefined ? text : replacement + text.slice(1);
}

interface NumeralMatch {
  value: number;
  /** The keraia consumed from the following token. */
  consumed: number;
}

function matchNumeral(
  tokens: readonly Token[],
  index: number,
  system: GreekSystem
): NumeralMatch | null {
  const token = tokens[index];
  if (system === "elot" || system === "classical" || token?.kind !== "word") {
    return null;
  }
  const before = tokens[index - 1];
  const after = tokens[index + 1];
  const thousands =
    before?.kind === "other" && before.text.endsWith(LOWER_KERAIA);
  const keraia = after?.kind === "other" && KERAIA.has(after.text.charAt(0));
  if (!(keraia || thousands)) {
    return null;
  }
  const value = numeralValue(token.glyphs, thousands);
  return value === null ? null : { value, consumed: keraia ? 1 : 0 };
}

export function transliterateGreek(
  text: string,
  system: GreekSystem,
  options: GreekOptions = { iotaSubscript: "omit" }
): string {
  const tokens = tokenizeGreek(text);
  let out = "";
  let skip = 0;
  for (const [index, token] of tokens.entries()) {
    if (token.kind === "other") {
      let piece = token.text.slice(skip);
      skip = 0;
      const numeralNext = matchNumeral(tokens, index + 1, system);
      if (numeralNext && piece.endsWith(LOWER_KERAIA)) {
        piece = piece.slice(0, -1);
      }
      out += convertPunctuation(piece, tokens[index - 1]?.kind === "word");
      continue;
    }
    const numeral = matchNumeral(tokens, index, system);
    if (numeral) {
      out += String(numeral.value);
      skip = numeral.consumed;
      continue;
    }
    out += romanizeWord(token.glyphs, system, options);
  }
  return out.normalize("NFC");
}

/* -------------------------------------------------------------- letter tables */

export type GreekNote =
  | "gamma-nasal"
  | "gk-edges"
  | "gk-always"
  | "mp-initial"
  | "mp-edges"
  | "nt-initial"
  | "upsilon-diphthong"
  | "elot-av"
  | "elot-ou"
  | "rough-h"
  | "rho-rh"
  | "rho-rh-classical"
  | "beta-ancient"
  | "iota-subscript"
  | "archaic";

export interface GreekLetterRow {
  greek: string;
  latin: string;
  note?: GreekNote;
}

const LETTER_ORDER = [..."αβγδεζηθικλμνξοπρστυφχψω"];

/** The values a system gives each letter and letter pair, for the reference table. */
export function greekLetterRows(system: GreekSystem): GreekLetterRow[] {
  const elot = system === "elot";
  const table = elot ? ELOT : PLAIN;
  const rows: GreekLetterRow[] = LETTER_ORDER.map((letter) => ({
    greek: `${letter.toUpperCase()} ${letter}${letter === "σ" ? " ς" : ""}`,
    latin: letter === "β" && system === "ala-lc-modern" ? "v" : table[letter],
  }));
  const pairs: GreekLetterRow[] = elot
    ? [
        { greek: "γγ, γξ, γχ", latin: "ng, nx, nch", note: "gamma-nasal" },
        { greek: "γκ", latin: "gk", note: "gk-always" },
        { greek: "μπ", latin: "b / mp", note: "mp-edges" },
        { greek: "ντ", latin: "nt" },
        { greek: "αι, ει, οι", latin: "ai, ei, oi" },
        {
          greek: "αυ, ευ, ηυ",
          latin: "av / af, ev / ef, iv / if",
          note: "elot-av",
        },
        { greek: "ου", latin: "ou", note: "elot-ou" },
      ]
    : [
        { greek: "γγ, γξ, γχ", latin: "ng, nx, nch", note: "gamma-nasal" },
        {
          greek: "γκ",
          latin: system === "classical" ? "nk" : "gk / nk",
          note: system === "classical" ? "gamma-nasal" : "gk-edges",
        },
        {
          greek: "αυ, ευ, ηυ, ου, υι",
          latin: "au, eu, ēu, ou, ui",
          note: "upsilon-diphthong",
        },
        { greek: "῾", latin: "h", note: "rough-h" },
        {
          greek: "ῥ",
          latin: "rh",
          note: system === "classical" ? "rho-rh-classical" : "rho-rh",
        },
        {
          greek: "ᾳ, ῃ, ῳ",
          latin: system === "classical" ? "a, ē, ō / ai, ēi, ōi" : "a, ē, ō",
          note: "iota-subscript",
        },
        { greek: "ϝ, ϙ", latin: `w, k${DOT_BELOW}`, note: "archaic" },
      ];
  if (system === "ala-lc-modern" || system === "ala-lc-ancient") {
    pairs.splice(
      2,
      0,
      { greek: "μπ", latin: "b / mp", note: "mp-initial" },
      { greek: "ντ", latin: `d${LOW_LINE} / nt`, note: "nt-initial" }
    );
  }
  if (system === "ala-lc-ancient") {
    rows[1] = { ...rows[1], note: "beta-ancient" };
  }
  return [...rows, ...pairs].map((row) => ({
    ...row,
    latin: row.latin.normalize("NFC"),
  }));
}
